import {
   computeDerivedValues,
   type MeasurementEntry,
   type MetricDefinition,
} from '@fasting-timer/shared'

import { getLocale, translate } from '../../i18n'
import { getEntryTimestampMs } from '../../state/measurementsStore'
import { unitSystem } from '../../state/unitSystemStore'
import {
   formatDurationSeconds,
   formatPace,
   formatQuantity,
   formatSpeed,
   getUnitLabel,
} from '../../utils/units'

/**
 * Formats a distance in metres in the active unit system (e.g. `2.0 km`).
 *
 * @param metres - The distance.
 * @param digits - Fraction digits (default 2).
 * @returns The formatted distance.
 */
export function formatDistance(metres: number, digits = 2) {
   return formatQuantity('distance', metres, unitSystem(), {
      digits,
      locale: getLocale(),
   })
}

/**
 * Formats a run-effort score as a plain number (one decimal), for chart axes.
 *
 * @param value - The effort score.
 * @returns The formatted number.
 */
export function formatEffort(value: number) {
   return value.toLocaleString(getLocale(), { maximumFractionDigits: 1 })
}

/**
 * Formats a primary/derived value by key: pace, speed, distance, duration, or effort.
 *
 * @param key - The field or derived key.
 * @param value - The SI value.
 * @returns The formatted value.
 */
export function formatMetricValue(key: string, value: number) {
   switch (key) {
      case 'pace':
         return formatPace(value, unitSystem())
      case 'speed':
         return formatSpeed(value, unitSystem(), getLocale())
      case 'distance':
         return formatDistance(value)
      case 'duration':
         return formatDurationSeconds(value)
      case 'effort':
         return translate('{value} effort', { value: formatEffort(value) })
      default:
         return value.toLocaleString(getLocale(), { maximumFractionDigits: 2 })
   }
}

/**
 * Formats an entry as `distance · duration · pace · speed` for lists and cards.
 *
 * @param entry - The entry.
 * @param definition - The metric definition.
 * @param derivedKeys - Which derived values to include (default: all).
 * @returns The formatted parts.
 */
export function describeEntry(
   entry: MeasurementEntry,
   definition: MetricDefinition,
   derivedKeys: readonly string[] = Object.keys(definition.derived),
) {
   const derived = computeDerivedValues(definition, entry.values)
   const parts = definition.fields.map(field =>
      formatMetricValue(field.key, entry.values[field.key] ?? 0),
   )
   for (const key of derivedKeys) {
      const value = derived[key]
      if (value !== null && value !== undefined) {
         parts.push(formatMetricValue(key, value))
      }
   }
   return parts
}

/**
 * Formats an entry timestamp as a short localized date-time.
 *
 * @param entry - The entry.
 * @returns The localized string.
 */
export function formatEntryTimestamp(entry: MeasurementEntry) {
   return new Date(getEntryTimestampMs(entry)).toLocaleString(getLocale(), {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   })
}

/**
 * Returns the distance unit label for the active unit system.
 *
 * @returns `km` or `mi`.
 */
export function distanceUnitLabel() {
   return getUnitLabel('distance', unitSystem())
}
